import React, { useState, useRef, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import L from "leaflet";
import moment from "moment";
import { useGetAllDeviceLocations } from "../../../api/routes/devices";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Loader2, MapPin, Navigation, Clock, Zap, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Create custom cluster icon
const createClusterCustomIcon = (cluster: any) => {
    const count = cluster.getChildCount();

    return L.divIcon({
        html: `<div style="
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background-color: rgba(0, 123, 255, 0.8);
                    color: white;
                    font-size: 14px;
                    font-weight: bold;
                    border: 2px solid #ffffff;
                ">
                    ${count}
               </div>`,
        className: "",
        iconSize: [40, 40],
    });
};

// Create device marker icon based on status
const createDeviceIcon = (device: any, isSelected: boolean = false) => {
    const isOnline = device.onlineStatus === "online";
    const baseColor = isOnline ? "#10b981" : "#ef4444"; // green for online, red for offline
    const size = isSelected ? 32 : 24; // larger when selected
    const borderColor = isSelected ? "#3b82f6" : "#ffffff"; // blue border when selected
    const borderWidth = isSelected ? "3px" : "2px";
    const shadow = isSelected ? "0 4px 12px rgba(59, 130, 246, 0.4)" : "0 2px 4px rgba(0,0,0,0.2)";

    return L.divIcon({
        html: `<div style="
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: ${size}px;
                    height: ${size}px;
                    border-radius: 50%;
                    background-color: ${baseColor};
                    border: ${borderWidth} solid ${borderColor};
                    box-shadow: ${shadow};
                    position: relative;
                    ${isSelected ? "animation: pulse 2s infinite;" : ""}
                ">
                    <svg width="${size * 0.5}" height="${size * 0.5}" viewBox="0 0 24 24" fill="white">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
               </div>
               ${isSelected ? "<style>@keyframes pulse { 0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7); } 70% { box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); } 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); } }</style>" : ""}`,
        className: "",
        iconSize: [size, size],
    });
};

interface DeviceWithLocation {
    device: any;
    location: any;
}

const LocationsRoute = () => {
    const { data: deviceLocations, isLoading, error } = useGetAllDeviceLocations();
    const [selectedDevice, setSelectedDevice] = useState<DeviceWithLocation | null>(null);
    const [hoveredDevice, setHoveredDevice] = useState<DeviceWithLocation | null>(null);
    const [mobileSelectedDevice, setMobileSelectedDevice] = useState<DeviceWithLocation | null>(null);
    const [map, setMap] = useState<any>(null);
    const [isMobile, setIsMobile] = useState(false);
    const navigate = useNavigate();
    const sidebarRef = useRef<HTMLDivElement>(null);

    // Check if mobile screen
    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);
        return () => window.removeEventListener("resize", checkScreenSize);
    }, []);

    // Center map on all markers when data loads
    useEffect(() => {
        if (map && deviceLocations && deviceLocations.length > 0) {
            const bounds = L.latLngBounds(deviceLocations.map((item) => [parseFloat(item.location.latitude), parseFloat(item.location.longitude)]));
            map.fitBounds(bounds, { padding: [20, 20] });
        }
    }, [map, deviceLocations]);

    const handleDeviceClick = (deviceWithLocation: DeviceWithLocation) => {
        if (isMobile) {
            // On mobile, open modal instead of selecting in sidebar
            setMobileSelectedDevice(deviceWithLocation);
        } else {
            // Desktop behavior
            setSelectedDevice(deviceWithLocation);

            // Center map on selected device
            if (map && deviceWithLocation.location) {
                const { latitude, longitude } = deviceWithLocation.location;
                // map.setView([parseFloat(latitude), parseFloat(longitude)], 16, {
                //     animate: true,
                //     duration: 0.5,
                // });
            }

            // Scroll sidebar to selected device
            // const deviceElement = document.getElementById(`device-${deviceWithLocation.device.id}`);
            // if (deviceElement && sidebarRef.current) {
            //     deviceElement.scrollIntoView({ behavior: "smooth", block: "center" });
            // }
        }
    };

    const handleDeviceNavigate = (deviceId: string) => {
        navigate(`/devices/${deviceId}`);
    };

    const handleLiveView = (deviceId: string) => {
        navigate(`/devices/${deviceId}/live`);
    };

    const handleDeviceHover = (deviceWithLocation: DeviceWithLocation | null) => {
        setHoveredDevice(deviceWithLocation);

        // Scroll sidebar to hovered device if hovering from map
        // if (deviceWithLocation) {
        //     const deviceElement = document.getElementById(`device-${deviceWithLocation.device.id}`);
        //     if (deviceElement && sidebarRef.current) {
        //         deviceElement.scrollIntoView({ behavior: "smooth", block: "center" });
        //     }
        // }
    };

    const getStatusColor = (onlineStatus: string) => {
        switch (onlineStatus) {
            case "online":
                return "bg-green-100 text-green-800";
            case "offline":
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="flex flex-col items-center space-y-4">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <p className="text-gray-600">Loading device locations...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <p className="text-red-600 mb-4">Failed to load device locations</p>
                    <Button onClick={() => window.location.reload()}>Retry</Button>
                </div>
            </div>
        );
    }

    if (!deviceLocations || deviceLocations.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Device Locations Found</h3>
                    <p className="text-gray-600">No devices with location data found in the last 24 hours.</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="flex" style={{ height: "calc(100vh - 64px)" }}>
                {/* Side Panel - Hidden on Mobile */}
                {!isMobile && (
                    <div ref={sidebarRef} className="w-96 bg-white border-r border-gray-200 overflow-y-auto ">
                        {/* <div className="p-6 border-b border-gray-200">
                            <h1 className="text-2xl font-bold text-gray-900">Device Locations</h1>
                            <p className="text-sm text-gray-600 mt-1">
                                {deviceLocations.length} device{deviceLocations.length !== 1 ? "s" : ""} with location data
                            </p>
                        </div> */}

                        <div className="space-y-2 p-4 h-full">
                            {deviceLocations.map((item) => (
                                <Card key={item.device.id} id={`device-${item.device.id}`} className={`p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${selectedDevice?.device.id === item.device.id ? "ring-2 ring-blue-500 bg-blue-50" : hoveredDevice?.device.id === item.device.id ? "ring-2 ring-blue-300 bg-blue-25" : "hover:bg-gray-50"}`} onClick={() => handleDeviceClick(item)} onMouseEnter={() => handleDeviceHover(item)} onMouseLeave={() => handleDeviceHover(null)}>
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="min-w-0 flex-1">
                                            <h3 className="font-semibold text-gray-900 truncate">{item.device.name}</h3>
                                            <p className="text-sm text-gray-500 truncate">{item.device.business?.name}</p>
                                        </div>
                                        <Badge className={`${getStatusColor(item.device.onlineStatus)} text-xs`}>
                                            {item.device.onlineStatus === "online" ? <Zap className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
                                            {item.device.onlineStatus === "online" ? "Online" : "Offline"}
                                        </Badge>
                                    </div>

                                    <div className="text-xs text-gray-500 mb-3">
                                        <div className="flex items-center space-x-4">
                                            {/* <span>
                                        {parseFloat(item.location.latitude).toFixed(4)},{parseFloat(item.location.longitude).toFixed(4)}
                                    </span> */}
                                            <span>Last seen {moment(parseInt(item.location.timestamp)).format("MMM D, HH:mm")}</span>
                                        </div>
                                        {item.location?.address && <p className="mt-1 text-gray-400 truncate">{item.location?.address}</p>}
                                    </div>

                                    <div className="flex space-x-2">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeviceNavigate(item.device.id);
                                            }}
                                            className="flex-1"
                                        >
                                            <Navigation className="w-3 h-3 mr-1" />
                                            View Details
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleLiveView(item.device.id);
                                            }}
                                            className="flex-1"
                                        >
                                            <Zap className="w-3 h-3 mr-1" />
                                            Live View
                                        </Button>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>
                )}

                {/* Map */}
                <div className="flex-1 relative">
                    <MapContainer center={[0, 0]} zoom={2} className="h-full w-full" whenReady={(mapInstance) => setMap(mapInstance.target)}>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                        {/* <MarkerClusterGroup animate={true} showCoverageOnHover={true} iconCreateFunction={createClusterCustomIcon}> */}
                        {deviceLocations.map((item) => {
                            const { latitude, longitude } = item.location;
                            const position = [parseFloat(latitude), parseFloat(longitude)];

                            return (
                                <Marker
                                    key={item.device.id}
                                    position={position}
                                    icon={createDeviceIcon(item.device, selectedDevice?.device.id === item.device.id)}
                                    eventHandlers={{
                                        click: () => handleDeviceClick(item),
                                        mouseover: () => handleDeviceHover(item),
                                        mouseout: () => handleDeviceHover(null),
                                    }}
                                >
                                    <Popup>
                                        <div className="min-w-[200px]">
                                            <h4 className="font-semibold text-gray-900 mb-1">{item.device.name}</h4>
                                            <p className="text-sm text-gray-600 mb-2">{item.device.business?.name}</p>
                                            <div className="text-xs text-gray-500 mb-2">
                                                <p>Last seen: {moment(parseInt(item.location.timestamp)).format("MMM D, YYYY HH:mm")}</p>
                                                {item.location?.address && <p className="mt-1">Near: {item.location?.address}</p>}
                                            </div>
                                            <div className="flex space-x-2">
                                                <Button size="sm" variant="outline" onClick={() => handleDeviceNavigate(item.device.id)}>
                                                    View Details
                                                </Button>
                                                <Button size="sm" onClick={() => handleLiveView(item.device.id)}>
                                                    Live View
                                                </Button>
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            );
                        })}
                        {/* </MarkerClusterGroup> */}
                    </MapContainer>
                </div>
            </div>

            {/* Mobile Device Modal */}
            {isMobile && (
                <Dialog open={!!mobileSelectedDevice} onOpenChange={() => setMobileSelectedDevice(null)}>
                    <DialogContent className="max-w-sm mx-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center justify-between">
                                <span>{mobileSelectedDevice?.device.name}</span>
                                <Badge className={`${getStatusColor(mobileSelectedDevice?.device.onlineStatus || "")} text-xs`}>
                                    {mobileSelectedDevice?.device.onlineStatus === "online" ? <Zap className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
                                    {mobileSelectedDevice?.device.onlineStatus}
                                </Badge>
                            </DialogTitle>
                        </DialogHeader>

                        {mobileSelectedDevice && (
                            <div className="space-y-4">
                                <div>
                                    <p className="text-sm text-gray-600">{mobileSelectedDevice.device.business?.name}</p>
                                </div>

                                <div className="text-sm text-gray-500">
                                    <p className="mb-1">
                                        <strong>Last seen:</strong> {moment(parseInt(mobileSelectedDevice.location.timestamp)).format("MMM D, YYYY HH:mm")}
                                    </p>
                                    {mobileSelectedDevice.location?.address && (
                                        <p className="text-gray-400">
                                            <strong>Location:</strong> {mobileSelectedDevice.location.address}
                                        </p>
                                    )}
                                </div>

                                <div className="flex space-x-2 pt-4">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                            handleDeviceNavigate(mobileSelectedDevice.device.id);
                                            setMobileSelectedDevice(null);
                                        }}
                                        className="flex-1"
                                    >
                                        <Navigation className="w-4 h-4 mr-2" />
                                        View Details
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            handleLiveView(mobileSelectedDevice.device.id);
                                            setMobileSelectedDevice(null);
                                        }}
                                        className="flex-1"
                                    >
                                        <Zap className="w-4 h-4 mr-2" />
                                        Live View
                                    </Button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};

export { LocationsRoute };
